import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import LoadingDots from '@/components/feature/LoadingDots';
import SocialFeed from './components/SocialFeed';
import SquadChatPanel from './components/SquadChatPanel';
import ChallengesBoard from './components/ChallengesBoard';
import NotificationsPanel from './components/NotificationsPanel';
import {
  fetchMemberNotifications,
  fetchMemberPushConfiguration,
  markAllMemberNotificationsRead,
  markMemberNotificationRead,
  fetchPublicChallenges,
  fetchPublicCommunityStats,
  type MemberNotificationItem,
  type PublicChallengeItem,
  type PublicCommunityStats
} from '@/features/public/api';
import { readStoredMemberUser, restoreMemberAccessToken, subscribeToMemberSessionChanges } from '@/features/public/memberSession';
import { disableMemberPushNotifications, enableMemberPushNotifications, supportsMemberPushNotifications } from '@/shared/push/memberPushNotifications';
import { useRealtimeInvalidation } from '@/shared/realtime/useRealtimeInvalidation';

type Tab = 'feed' | 'chat' | 'challenges';

const tabs: { id: Tab; label: string; icon: string }[] = [
  { id: 'feed', label: 'Fil d\'actualité', icon: 'ri-home-4-line' },
  { id: 'chat', label: 'Discussions privées', icon: 'ri-chat-3-line' },
  { id: 'challenges', label: 'Défis', icon: 'ri-trophy-line' },
];

const numberFormatter = new Intl.NumberFormat("fr-FR");

const emptySocialStats: PublicCommunityStats = {
  activeMembers: 0,
  sportsPracticed: 0,
  activeTeams: 0,
  privateGroups: 0,
  activeChallenges: 0
};

export default function SocialPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const requestedTab = useMemo(() => {
    const rawTab = new URLSearchParams(location.search).get('tab');

    return rawTab === 'feed' || rawTab === 'chat' || rawTab === 'challenges'
      ? rawTab
      : null;
  }, [location.search]);
  const requestedChatId = useMemo(() => new URLSearchParams(location.search).get('chatId'), [location.search]);
  const [activeTab, setActiveTab] = useState<Tab>(requestedTab ?? 'feed');
  const [showNotifications, setShowNotifications] = useState(false);
  const [challengePreview, setChallengePreview] = useState<PublicChallengeItem[]>([]);
  const [communityStats, setCommunityStats] = useState<PublicCommunityStats | null>(null);
  const [challengePreviewLoading, setChallengePreviewLoading] = useState(true);
  const [memberAccessToken, setMemberAccessToken] = useState<string | null>(null);
  const [memberLoggedIn, setMemberLoggedIn] = useState(Boolean(readStoredMemberUser()));
  const [notifications, setNotifications] = useState<MemberNotificationItem[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushRequesting, setPushRequesting] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const pushSupported = useMemo(() => supportsMemberPushNotifications(), []);

  const loadChallengePreview = useCallback(async () => {
    setChallengePreviewLoading(true);
    const accessToken = await restoreMemberAccessToken().catch(() => null);

    const [stats, payload] = await Promise.all([
      fetchPublicCommunityStats().catch(() => emptySocialStats),
      fetchPublicChallenges({
        accessToken: accessToken ?? undefined
      }).catch(() => [])
    ]);

    setCommunityStats(stats);
    setChallengePreview(payload.slice(0, 3));
    setChallengePreviewLoading(false);
  }, []);

  const resolvedCommunityStats = communityStats ?? emptySocialStats;
  const communityStatsLoading = communityStats === null;

  useEffect(() => {
    if (requestedTab) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  useEffect(() => {
    void loadChallengePreview();
  }, [loadChallengePreview]);

  const syncSessionState = useCallback(async () => {
    const storedUser = readStoredMemberUser();
    setMemberLoggedIn(Boolean(storedUser));
    const restoredAccessToken = await restoreMemberAccessToken().catch(() => null);
    setMemberAccessToken(restoredAccessToken);
  }, []);

  useEffect(() => {
    void syncSessionState();

    const unsubscribe = subscribeToMemberSessionChanges(() => {
      void syncSessionState();
    });

    return () => {
      unsubscribe();
    };
  }, [syncSessionState]);

  const loadNotifications = useCallback(async () => {
    if (!memberAccessToken) {
      setNotifications([]);
      setPushEnabled(false);
      setNotificationsLoading(false);
      return;
    }

    setNotificationsLoading(true);

    try {
      const [items, pushConfiguration] = await Promise.all([
        fetchMemberNotifications(memberAccessToken),
        fetchMemberPushConfiguration(memberAccessToken)
      ]);

      setNotifications(items);
      setPushEnabled(
        pushSupported
        && typeof Notification !== "undefined"
        && Notification.permission === "granted"
        && pushConfiguration.enabled
      );
      setPushError(null);
    } catch (caughtError) {
      setPushError(caughtError instanceof Error ? caughtError.message : "Impossible de charger les notifications.");
    } finally {
      setNotificationsLoading(false);
    }
  }, [memberAccessToken, pushSupported]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  useRealtimeInvalidation({
    topics: ["community-stats", "challenges"],
    onInvalidate: () => {
      void loadChallengePreview();
    }
  });

  useRealtimeInvalidation({
    topics: ["notifications"],
    accessToken: memberAccessToken,
    requiresAuth: true,
    enabled: Boolean(memberAccessToken),
    onInvalidate: () => {
      void loadNotifications();
    }
  });

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tab);

    if (tab !== 'chat') {
      searchParams.delete('chatId');
    }

    navigate(
      {
        pathname: location.pathname,
        search: `?${searchParams.toString()}`
      },
      { replace: true }
    );
  };

  const unreadCount = notifications.filter((notification) => !notification.read).length;

  const handleEnablePush = useCallback(async () => {
    if (!memberAccessToken) {
      navigate('/formulaires?tab=login&redirect=%2Fsocial');
      return;
    }

    setPushRequesting(true);
    setPushError(null);

    try {
      await enableMemberPushNotifications(memberAccessToken);
      await loadNotifications();
    } catch (caughtError) {
      setPushError(caughtError instanceof Error ? caughtError.message : "Impossible d'activer les notifications push.");
    } finally {
      setPushRequesting(false);
    }
  }, [loadNotifications, memberAccessToken, navigate]);

  const handleDisablePush = useCallback(async () => {
    if (!memberAccessToken) {
      return;
    }

    setPushRequesting(true);
    setPushError(null);

    try {
      await disableMemberPushNotifications(memberAccessToken);
      await loadNotifications();
    } catch (caughtError) {
      setPushError(caughtError instanceof Error ? caughtError.message : "Impossible de désactiver les notifications push.");
    } finally {
      setPushRequesting(false);
    }
  }, [loadNotifications, memberAccessToken]);

  const handleMarkAllNotificationsRead = useCallback(async () => {
    if (!memberAccessToken || unreadCount === 0) {
      return;
    }

    setNotifications((currentNotifications) =>
      currentNotifications.map((notification) => ({
        ...notification,
        read: true
      }))
    );

    try {
      await markAllMemberNotificationsRead(memberAccessToken);
    } catch {
      await loadNotifications();
    }
  }, [loadNotifications, memberAccessToken, unreadCount]);

  const handleOpenNotification = useCallback(async (notification: MemberNotificationItem) => {
    if (memberAccessToken && !notification.read) {
      setNotifications((currentNotifications) =>
        currentNotifications.map((currentNotification) =>
          currentNotification.id === notification.id
            ? {
                ...currentNotification,
                read: true
              }
            : currentNotification
        )
      );

      try {
        await markMemberNotificationRead(memberAccessToken, notification.id);
      } catch {
        await loadNotifications();
      }
    }

    setShowNotifications(false);

    if (notification.linkUrl) {
      navigate(notification.linkUrl);
    }
  }, [loadNotifications, memberAccessToken, navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Hero */}
      <section className="relative py-14 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20african%20sports%20club%20members%20socializing%20together%20outdoor%20senegal%20vibrant%20community%20gathering%20celebration%20energy&width=1600&height=400&seq=social_hero&orientation=landscape"
            alt="Réseau Social ASC"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#E8521A]/85 to-[#1A8C7A]/70"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-3">Réseau Social ASC</h1>
              <p className="text-lg opacity-90 max-w-xl">
                Connectez-vous, partagez vos performances, discutez avec vos squads et vos équipes, puis relevez des défis sportifs !
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-5 py-3 text-white text-center">
                <p className="text-2xl font-bold">
                  {communityStatsLoading ? <LoadingDots className="h-8" dotClassName="h-2 w-2" label="Chargement membres actifs" /> : numberFormatter.format(resolvedCommunityStats.activeMembers)}
                </p>
                <p className="text-xs opacity-80">Membres actifs</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-5 py-3 text-white text-center">
                <p className="text-2xl font-bold">
                  {communityStatsLoading ? <LoadingDots className="h-8" dotClassName="h-2 w-2" label="Chargement groupes privés" /> : numberFormatter.format(resolvedCommunityStats.privateGroups)}
                </p>
                <p className="text-xs opacity-80">Groupes privés</p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-5 py-3 text-white text-center">
                <p className="text-2xl font-bold">
                  {communityStatsLoading ? <LoadingDots className="h-8" dotClassName="h-2 w-2" label="Chargement défis actifs" /> : numberFormatter.format(resolvedCommunityStats.activeChallenges)}
                </p>
                <p className="text-xs opacity-80">Défis actifs</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="sticky top-20 z-40 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-2 py-4 px-4 font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer text-sm ${
                    activeTab === tab.id
                      ? 'border-[#E8521A] text-[#E8521A]'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className={tab.icon}></i>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
            {/* Notifications Bell */}
            <div className="relative flex-shrink-0 ml-4">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-notification-3-line text-xl"></i>
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#E8521A] text-white text-xs flex items-center justify-center font-bold leading-none">
                    {unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 z-50">
                  <NotificationsPanel
                    notifications={notifications}
                    loading={notificationsLoading}
                    memberLoggedIn={memberLoggedIn}
                    pushSupported={pushSupported}
                    pushEnabled={pushEnabled}
                    pushRequesting={pushRequesting}
                    pushError={pushError}
                    onEnablePush={handleEnablePush}
                    onDisablePush={handleDisablePush}
                    onMarkAllRead={handleMarkAllNotificationsRead}
                    onOpenNotification={handleOpenNotification}
                    onClose={() => setShowNotifications(false)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'feed' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Feed */}
            <div className="lg:col-span-2">
              <SocialFeed />
            </div>
            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h3 className="font-bold text-gray-900 text-sm mb-4">Mon profil sportif</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-trophy-line text-amber-500"></i>
                      <span className="text-sm text-gray-600">Défis rejoints</span>
                    </div>
                    <span className="font-bold text-gray-900 text-sm">0</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-team-line text-teal-600"></i>
                      <span className="text-sm text-gray-600">Mes groupes privés</span>
                    </div>
                    <span className="font-bold text-gray-900 text-sm">3</span>
                  </div>
                </div>
              </div>

              {/* Active Challenges Preview */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900 text-sm">Défis actifs</h3>
                  <button onClick={() => handleTabChange('challenges')} className="text-xs text-[#E8521A] hover:underline cursor-pointer">Voir tout</button>
                </div>
                <div className="space-y-3">
                  {challengePreview.length > 0 ? (
                    challengePreview.map((challenge) => (
                      <div key={challenge.id} className="flex items-center gap-3">
                        <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${challenge.bgColor} flex-shrink-0`}>
                          <i className={`${challenge.icon} ${challenge.color}`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{challenge.title}</p>
                          <p className="text-xs text-gray-400">{challenge.participants} participants</p>
                        </div>
                      </div>
                    ))
                  ) : challengePreviewLoading ? (
                    <div className="rounded-xl border border-dashed border-gray-200 px-4 py-4 text-xs text-gray-500">
                      Chargement des défis...
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-gray-200 px-4 py-4 text-xs text-gray-500">
                      Aucun défi dynamique disponible pour le moment.
                    </div>
                  )}
                </div>
              </div>

              {/* Notifications Preview */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900 text-sm">Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="w-5 h-5 rounded-full bg-[#E8521A] text-white text-xs flex items-center justify-center font-bold">{unreadCount}</span>
                  )}
                </div>
                <div className="space-y-3">
                  {memberLoggedIn ? (
                    notifications.slice(0, 3).map((notification) => (
                      <button
                        key={notification.id}
                        onClick={() => void handleOpenNotification(notification)}
                        className={`flex w-full items-start gap-2.5 text-left ${!notification.read ? 'opacity-100' : 'opacity-60'}`}
                      >
                        {notification.avatarUrl ? (
                          <img src={notification.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover object-[center_24%] flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                            <i className="ri-notification-3-line text-[#E8521A] text-sm"></i>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-800 leading-snug line-clamp-1">{notification.title}</p>
                          <p className="text-xs text-gray-700 leading-snug line-clamp-2">{notification.body}</p>
                        </div>
                        {!notification.read && <span className="w-2 h-2 rounded-full bg-[#E8521A] flex-shrink-0 mt-1"></span>}
                      </button>
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-gray-200 px-4 py-4 text-xs text-gray-500">
                      Connectez-vous pour voir vos notifications et activer les alertes push.
                    </div>
                  )}
                  {memberLoggedIn && notifications.length === 0 && !notificationsLoading && (
                    <div className="rounded-xl border border-dashed border-gray-200 px-4 py-4 text-xs text-gray-500">
                      Aucune notification pour le moment.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Discussions privées</h2>
              <p className="text-gray-500 text-sm mt-1">Communiquez en temps réel dans les espaces privés de vos squads et de vos équipes</p>
            </div>
            <SquadChatPanel initialChatId={requestedChatId} />
          </div>
        )}

        {activeTab === 'challenges' && (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Défis Sportifs</h2>
                <p className="text-gray-500 text-sm mt-1">Relevez des défis, grimpez dans le classement et gagnez des récompenses</p>
              </div>
              <div className="bg-amber-50 rounded-xl px-4 py-2 flex items-center gap-2">
                <i className="ri-trophy-line text-amber-500"></i>
                <span className="text-sm font-semibold text-amber-700">{communityStats.activeChallenges} défis actifs</span>
              </div>
            </div>
            <ChallengesBoard />
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
}
